import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { track } from "@/lib/track";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { X, Gift } from "lucide-react";

const SEEN_KEY = "ns_exit_intent_ts";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // once per 7 days
const MOBILE_DELAY_MS = 35000; // mobile: time-based threshold

function recentlyShown() {
  try {
    if (localStorage.getItem("ns_lead_submitted")) return true;
    const ts = Number(localStorage.getItem(SEEN_KEY) || 0);
    return ts && Date.now() - ts < COOLDOWN_MS;
  } catch { return false; }
}

// Shows a one-time lead-magnet popup on exit intent (desktop: mouse leaves the
// top of the viewport; mobile: after a dwell delay). Frequency-capped to once
// per 7 days and suppressed once a lead has been submitted.
export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (recentlyShown()) return;
    let fired = false;
    const trigger = () => {
      if (fired || recentlyShown()) return;
      fired = true;
      try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* noop */ }
      setOpen(true);
      track("exit_intent_shown", {});
      cleanup();
    };
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    const isMobile = window.innerWidth < 768;
    let timer;
    if (isMobile) {
      timer = setTimeout(trigger, MOBILE_DELAY_MS);
    } else {
      document.addEventListener("mouseout", onMouseOut);
    }
    function cleanup() {
      document.removeEventListener("mouseout", onMouseOut);
      if (timer) clearTimeout(timer);
    }
    return cleanup;
  }, []);

  const close = () => { setOpen(false); track("exit_intent_dismissed", {}); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[80] grid place-items-center p-4"
          style={{ background: "rgba(20,30,26,0.55)", backdropFilter: "blur(4px)" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close} data-testid="exit-intent-popup">
          <motion.div initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[var(--ns-surface)] rounded-[var(--ns-radius)] border border-[var(--ns-border)] p-7 ns-card">
            <button onClick={close} data-testid="exit-intent-close" className="absolute top-4 right-4 text-[var(--ns-subtle)] hover:text-[var(--ns-text)]"><X size={20} /></button>
            <div className="w-14 h-14 rounded-2xl grid place-items-center mb-4" style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
              <Gift size={28} />
            </div>
            <h3 className="font-head text-2xl font-bold">Before you go — grab the free checklist</h3>
            <p className="text-sm text-[var(--ns-muted)] mt-2 mb-5">
              Our Math Readiness Checklist helps you spot gaps before they snowball — plus a short, practical 5-day "Math at Home" series. No spam, unsubscribe anytime.
            </p>
            <LeadCaptureForm source="exit_intent" magnet="math_readiness_checklist" compact />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
